import { useState } from 'react';
import { Form, Button, Table, Alert, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useUpdateRentMutation } from '../../slices/rentsApiSlice';

const RentEditImportScreen = () => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [updateRent, { isLoading: loadingUpdate }] = useUpdateRentMutation();

  const isValidObjectId = (id) => /^[a-f\d]{24}$/i.test(id);

  const validateRow = (row) => {
    if (!row._id || !isValidObjectId(row._id.trim())) return 'Invalid or missing ID';
    if (!row.house || !row.tenant || !row.landlord || !row.startDate || !row.endDate || !row.monthlyRent || !row.numberOfMonths || !row.address || !row.date) {
      return 'Missing required fields';
    }
    return null;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsed = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const enriched = parsed.map((row, index) => ({
        index,
        _id: row._id?.trim(),
        house: row.house,
        tenant: row.tenant,
        landlord: row.landlord,
        startDate: row.startDate,
        endDate: row.endDate,
        lastRentEnd: row.lastRentEnd,
        monthlyRent: row.monthlyRent,
        numberOfMonths: row.numberOfMonths,
        address: row.address,
        date: row.date,
        datePaid: row.datePaid,
        remark: row.remark,
        error: validateRow(row),
        status: null,
      }));

      setRows(enriched);
      toast.success(`Loaded ${enriched.length} rows from ${file.name}`);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleRowSubmit = async (row, index) => {
    if (row.error) {
      toast.warning(`Row ${index + 1} skipped: ${row.error}`);
      return;
    }

    try {
      await updateRent({
        rentId: row._id,
        updatedData: {
          house: row.house,
          tenant: row.tenant,
          landlord: row.landlord,
          startDate: row.startDate,
          endDate: row.endDate,
          lastRentEnd: row.lastRentEnd,
          monthlyRent: row.monthlyRent,
          numberOfMonths: row.numberOfMonths,
          address: row.address,
          date: row.date,
          datePaid: row.datePaid,
          remark: row.remark,
        },
      }).unwrap();

      const updated = [...rows];
      updated[index].status = { type: 'Success', message: 'Updated' };
      setRows(updated);
      toast.success(`Row ${index + 1} updated`);
    } catch (err) {
      const updated = [...rows];
      updated[index].status = {
        type: 'Failed',
        message: err?.data?.message || err.message,
      };
      setRows(updated);
      toast.error(`Row ${index + 1} failed: ${updated[index].status.message}`);
    }
  };

  const handleSubmitAll = async () => {
    const updatedRows = [...rows];

    for (let i = 0; i < updatedRows.length; i++) {
      const row = updatedRows[i];
      if (row.error) {
        updatedRows[i].status = { type: 'Skipped', message: row.error };
        continue;
      }

      try {
        await updateRent({
          rentId: row._id,
          updatedData: {
            house: row.house,
            tenant: row.tenant,
            landlord: row.landlord,
            startDate: row.startDate,
            endDate: row.endDate,
            lastRentEnd: row.lastRentEnd,
            monthlyRent: row.monthlyRent,
            numberOfMonths: row.numberOfMonths,
            address: row.address,
            date: row.date,
            datePaid: row.datePaid,
            remark: row.remark,
          },
        }).unwrap();

        updatedRows[i].status = { type: 'Success', message: 'Updated' };
      } catch (err) {
        updatedRows[i].status = {
          type: 'Failed',
          message: err?.data?.message || err.message,
        };
      }
    }

    setRows(updatedRows);
    toast.success('✅ All rent rows processed');
  };

  return (
    <div className="container mt-4">
      <h2>💼 Bulk Rent Update</h2>

      <Form.Group controlId="excelFile" className="mb-3">
        <Form.Label>Upload Excel File (.xlsx)</Form.Label>
        <Form.Control type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />
      </Form.Group>

      {rows.length > 0 && (
        <>
          <h5>Preview & Edit: {fileName}</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>ID</th>
                <th>Tenant</th>
                <th>House</th>
                <th>Landlord</th>
                <th>Start</th>
                <th>End</th>
                <th>Last End</th>
                <th>Months</th>
                <th>Rent</th>
                <th>Address</th>
                <th>Recorded</th>
                <th>Paid</th>
                <th>Remark</th>
                <th>Submit</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} style={{ background: row.error ? '#fff3cd' : 'inherit' }}>
                  <td><Form.Control value={row._id} readOnly plaintext /></td>
                  <td><Form.Control value={row.tenant} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].tenant = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control value={row.house} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].house = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control value={row.landlord} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].landlord = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control type="date" value={row.startDate} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].startDate = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control type="date" value={row.endDate} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].endDate = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control type="date" value={row.lastRentEnd} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].lastRentEnd = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control value={row.numberOfMonths} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].numberOfMonths = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control value={row.monthlyRent} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].monthlyRent = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control value={row.address} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].address = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control type="date" value={row.date} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].date = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control type="date" value={row.datePaid} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].datePaid = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control value={row.remark} onChange={(e) => {
                    const updated = [...rows];

                    updated[idx].remark = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleRowSubmit(row, idx)}
                      disabled={loadingUpdate}
                    >
                      {loadingUpdate ? <Spinner animation="border" size="sm" /> : 'Submit'}
                    </Button>
                  </td>
                  <td>
                    {row.status ? (
                      <span
                        className={
                          row.status.type === 'Success'
                            ? 'text-success'
                            : row.status.type === 'Skipped'
                            ? 'text-warning'
                            : 'text-danger'
                        }
                      >
                        {row.status.type} — {row.status.message}
                      </span>
                    ) : row.error ? (
                      <span className="text-danger">{row.error}</span>
                    ) : (
                      <span className="text-muted">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button
            variant="success"
            onClick={handleSubmitAll}
            disabled={loadingUpdate}
            className="mb-3"
          >
            {loadingUpdate ? <Spinner animation="border" size="sm" /> : '🚀 Submit All Valid Rows'}
          </Button>
        </>
      )}

      {rows.some((row) => row.status) && (
        <div className="mt-4">
          <h5>📋 Submission Summary</h5>
          {rows.map((row, i) =>
            row.status ? (
              <Alert
                key={i}
                variant={
                  row.status.type === 'Success'
                    ? 'success'
                    : row.status.type === 'Skipped'
                    ? 'warning'
                    : 'danger'
                }
              >
                Row {i + 1}: {row.status.type} — {row.status.message}
              </Alert>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

export default RentEditImportScreen;